import { createSelector } from 'reselect';

const selectBillingState = (state) => state.settings.billing;
const selectCreditCards = (state) => state.settings.billing.creditCards;
const selectZuoraProductState = createSelector(
  [selectBillingState],
  (billingState) => billingState.zuoraProducts,
);
const selectZuoraRatePlanState = createSelector(
  [selectBillingState],
  (billingState) => billingState.zuoraRatePlans,
);
const selectObjectManipulation = createSelector(
  [selectBillingState],
  (billing) => billing.zuoraObjectManipulation,
);

/*------------------------------------------------------------------------------------------------*/
/* Zuora Selectors                                                                            */
/*------------------------------------------------------------------------------------------------*/

// export const selectZuoraAccountId = createSelector([getZuoraAccountId]);

/**
 * selectZuoraProducts
 * Selector for the products obj.
 * @param { Object } state: The application state.
 * @returns { Object } Zuora products
 */
export const selectZuoraProducts = createSelector(
  [selectZuoraProductState],
  (productState) => productState.products,
);

/**
 * selectZuoraProductsLoading
 * Selector for the products isloading bool.
 * @param { Object } state: The application state.
 * @returns { Boolean } isLoading: if the products request is loading
 */
export const selectZuoraProductsLoading = createSelector(
  [selectZuoraProductState],
  (productState) => productState.isLoading,
);

/**
 * selectZuoraRatePlans
 * Selector for the rate plans obj.
 * @param { Object } state: The application state.
 * @returns { Object } Zuora Rate Plans for a product
 */
export const selectZuoraRatePlans = createSelector(
  [selectZuoraRatePlanState],
  (ratePlanState) => ratePlanState.ratePlans,
);

/**
 * selectZuoraRatePlansLoading
 * Selector for the rate plans isloading bool.
 * @param { Object } state: The application state.
 * @returns { Boolean } isLoading: if the rate plans request is loading
 */
export const selectZuoraRatePlansLoading = createSelector(
  [selectZuoraRatePlanState],
  (ratePlanState) => ratePlanState.isLoading,
);

/**
 * selectZuoraPaymentsObject
 * Selector for the dashboard state.
 * @param { Object } state: The application state.
 * @returns { Object } Zuora credentials needed to render the Zuora form.
 */
export const selectZuoraPaymentsObject = createSelector([selectBillingState], (billingState) => ({
  ...billingState,
}));

/**
 * selectTrialEndDate
 * Selector for the trial end date.
 * @param { Object } state: The application state.
 * @returns { number | null } The trial end date in milliseconds.
 */
export const selectTrialEndDate = createSelector(
  [selectBillingState],
  (billingState) => billingState.trialExpirationDate,
);

/**
 * selectPaymentPlanId
 * Selector for the payment plan id.
 * @param { Object } state: The application state.
 * @returns { number } The payment plan id.
 */
export const selectPaymentPlanId = createSelector(
  [selectBillingState],
  (billingState) => billingState.paymentPlanId,
);

/**
 * selectZuoraAccountId
 * Selector for the zuora account id.
 * @param { Object } state: The application state.
 * @returns { number } The payment plan id.
 */
export const selectZuoraAccountId = createSelector(
  [selectBillingState],
  (billingState) => billingState.zuoraAccountId,
);

/**
 * selectCreditCards
 * Selector for getting credit.
 * @param { Object } state: The application state.
 * @returns { number } The payment plan id.
 */
export const selectFormattedCreditCards = createSelector([selectCreditCards], (creditCards) =>
  creditCards
    ? creditCards.map((creditCard) => {
        const { cardType, cardNumber, expirationMonth, expirationYear, defaultPaymentMethod, id } =
          creditCard;
        return {
          cardType,
          cardNumber,
          expirationDate: `${expirationMonth}/${expirationYear}`,
          defaultPaymentMethod,
          id,
        };
      })
    : [],
);

/**
 * selectObjectManipulationLoading
 * Selector for getting if object manipulation is loading
 * @param { Object } state: The application state.
 * @returns { Boolean } If object manipulation is loading.
 */
export const selectObjectManipulationLoading = createSelector(
  [selectObjectManipulation],
  (objectManipulation) => objectManipulation.isLoading,
);
